import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import InputField from "./core-components/InputField.tsx";
import { useSelector } from "react-redux";
import { translations } from "../../utils/translations.js";
import { useAuth } from "../../hooks/useAuth.jsx";
import { login, resetPass } from "../../services/authService.js";
import { ToastContainer, toast } from "react-toastify";
import { roles } from "../../utils/constants.ts";
import { useSearchParams } from "react-router-dom";

// Styled Components
const Container = styled.div`
  background-color: #fff;
  border-radius: 10px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.25), 0 10px 10px rgba(0, 0, 0, 0.22);
  position: absolute;
  overflow: hidden;
  width: 768px;
  max-width: 70%;
  min-height: 480px;
  top: 15%;

  @media (max-width: 768px) {
    width: 300px;
  }
`;

const FormContainer = styled.div`
  position: absolute;
  width: 63%;
  top: 0;
  height: 100%;
  transition: all 0.6s ease-in-out;
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    padding-top: 20px;
  }
`;
const SignInContainer = styled.form`
  left: 0;
  height: 90%;
  width: 80%;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const OverlayContainer = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  width: 50%;
  height: 100%;
  overflow: hidden;

  z-index: 100;
  @media (max-width: 768px) {
    display: none;
  }
`;

const Overlay = styled.div`
  background: linear-gradient(
    to right,
    var(--fill-green),
    var(--tertiary-green)
  );
  color: #ffffff;
  position: relative;
  left: -100%;
  height: 100%;
  width: 200%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const OverlayPanel = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  top: 0;
  height: 100%;
  width: 50%;
  left: 50%;

  @media (max-width: 768px) {
    display: none;
  }
`;

const Button = styled.button`
  border-radius: 20px;
  border: 1px solid var(--primary-green);
  background-color: var(--secondary-green);
  color: #ffffff;
  font-size: 12px;
  font-weight: bold;
  padding: 12px 45px;
  letter-spacing: 1px;
  text-transform: uppercase;

  cursor: pointer;
`;

const PasswordVisibilityButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  background: none;
  border: none;
  color: var(--primary-brown);
  font-size: 16px;
  cursor: pointer;
`;
const GoButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-brown);
  font-size: 16px;
  cursor: pointer;

  @media (min-width: 768px) {
    display: none;
  }
`;

const notifyPassError = (msg) => {
  toast.error(msg, {
    position: "top-center",
    autoClose: 3000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });
};
const notifySuccess = (msg) => {
  toast.success(msg, {
    position: "top-center",
    autoClose: 2000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: false,
    draggable: false,
    progress: undefined,
    theme: "light",
  });
};

const ResetPasswordModal = () => {
  const languageCode = useSelector((state: any) => state.settings.languageCode);
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const token = decodeURIComponent(searchParams.get("token") || "");

  const [hide, setHide] = useState(true);
  const [password, setPassword] = useState("");
  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*(),.?":{}|<>])[A-Za-z\d!@#$%^&*(),.?":{}|<>]{8,}$/;
  const togglePasswordVisibility = () => setHide(!hide);
  //  notify(translations[languageCode]["login-error"]

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!passwordRegex.test(password)) {
      notifyPassError(translations[languageCode]["passwords-rule"]);
      return;
    } else {
      try {
        await resetPass({
          email,
          token,
          password,
        });
        notifySuccess(translations[languageCode]["reset-password-login-msg"]);
      } catch (error) {
        console.error("Error sending email", error);
        notifyPassError(translations[languageCode]["reset-error"]);
      }
    }
  };

  return (
    <Container>
      <FormContainer>
        <SignInContainer onSubmit={handleSubmit}>
          <h1> {translations[languageCode]["reset-password-title"]}</h1>

          <div style={{ position: "relative" }}>
            <InputField
              name="password"
              type={"password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              isRequired
              label="Password"
              placeholder="Pass123!@#"
              height="36px"
            />
          </div>
          <Button type="submit">
            {translations[languageCode]["reset-password-button"]}
          </Button>
        </SignInContainer>
      </FormContainer>

      <OverlayContainer>
        <Overlay>
          <OverlayPanel>
            <h1>Hello!</h1>

            <p>{translations[languageCode]["register-login-message"]}</p>
            <Button onClick={() => (window.location.href = "/login")}>
              {translations[languageCode]["go-login"]}
            </Button>
          </OverlayPanel>
        </Overlay>
      </OverlayContainer>
      <ToastContainer />
    </Container>
  );
};

export default ResetPasswordModal;
